/**
 * `TrpcMessageComposeAdapter` — production `MessageComposePort` adapter
 * (feature 027-member-message-compose, US1).
 *
 * Wraps the typed tRPC client (`WebTrpcClient`, typed via `AppRouter`) and
 * forwards each port method to the matching `memberMessaging.*` procedure:
 *
 *   - `listTopics()`  → `client.memberMessaging.listTopics.query()`
 *   - `submit(input)` → `client.memberMessaging.submitMessage.mutate(input)`
 *
 * Return shapes are field-for-field identical to the API's `TopicDefinition`
 * and `SendResult` — the wire-level mapping is the tRPC envelope (no
 * transformer is configured, so JSON shapes round-trip directly), and the
 * function bodies below let TypeScript verify that the inferred client return
 * matches the port-declared web-local shape at this seam. A drift between the
 * API contract and the web-local mirror surfaces here as a compile error, not
 * a silent runtime mismatch.
 *
 * Construction mirrors `TrpcContentReader`: the adapter takes a
 * `WebTrpcClient` via constructor injection. Production wiring builds the
 * client via `createWebTrpcClient()` once per React root and hands it in;
 * tests pass a hand-rolled mock that satisfies the subset of `WebTrpcClient`
 * the adapter uses (`memberMessaging.listTopics.query` /
 * `memberMessaging.submitMessage.mutate`). No real network in unit tests.
 *
 * This adapter is the application default and the ONLY compose adapter wired at
 * the composition root (050 US1): `useMessageComposePort` constructs it once per
 * mount, and the `memberMessaging` namespace is mounted, so the send is real.
 * `DemoMessageComposeAdapter` is retained ONLY as a test fixture (its topic tree
 * + configurable submit result), injected through the hook's `{ port }` seam by
 * wizard/UI harnesses; production never falls back to the browser demo.
 */

import type { MessageKeys } from '../../i18n/messages/index.js';
import type { WebTrpcClient } from '../../trpc/client.js';
import type {
  ComposeSubmitInput,
  ComposeSubmitResult,
  MessageComposePort,
  TopicDefinition,
} from './MessageComposePort.js';

/**
 * `WidenKeys<T>` rewrites every `MessageKeys` slot in `T` back to `string`,
 * recursing through arrays and objects (including the `Field.reveals`
 * `Field[] | GuidanceDeadEnd` union). `ApiTopicShape` is therefore the web
 * `TopicDefinition` with ONLY the localized slots (label / intro / attachment
 * hint / dead-end text) widened to `string` — exactly what the api emits. The
 * `satisfies` in `listTopics` checks the wire return against it, so any
 * NON-localized structural drift (renamed/added/removed field, changed
 * `required`/`type`) still fails to compile at this seam; only the deliberate
 * key-narrowing is asserted away.
 */
type WidenKeys<T> = T extends MessageKeys
  ? string
  : T extends readonly (infer E)[]
    ? readonly WidenKeys<E>[]
    : T extends object
      ? { readonly [K in keyof T]: WidenKeys<T[K]> }
      : T;
type ApiTopicShape = WidenKeys<TopicDefinition>;

export class TrpcMessageComposeAdapter implements MessageComposePort {
  readonly #client: WebTrpcClient;

  /**
   * @param client - the typed tRPC client this adapter delegates to.
   *   Production wiring builds it via `createWebTrpcClient()`; tests pass a
   *   hand-rolled mock satisfying the subset of {@link WebTrpcClient} used here.
   */
  constructor(client: WebTrpcClient) {
    this.#client = client;
  }

  /**
   * The `memberMessaging` namespace is OPTIONAL on `AppRouter` (it is a
   * deferred-and-retained mount, same posture as `jobs`), so the typed
   * client surfaces it as `… | undefined`. This adapter is wired only when the
   * namespace is the live source; if it is absent the adapter was mis-wired —
   * surface that as a clear error rather than a runtime "cannot read property
   * of undefined". The guard also narrows the type for the call sites below.
   */
  #namespace(): NonNullable<WebTrpcClient['memberMessaging']> {
    const ns = this.#client.memberMessaging;
    if (ns === undefined) {
      throw new Error(
        'TrpcMessageComposeAdapter: the `memberMessaging` tRPC namespace is not mounted on this client. Wire this adapter only when the namespace is live.',
      );
    }
    return ns;
  }

  async listTopics(): Promise<readonly TopicDefinition[]> {
    // Boundary narrowing (D-117): the api types the localized slots
    // (label / intro / attachment hint / dead-end text) as `string` because it
    // emits i18n KEY strings there; the web-local `TopicDefinition` narrows them
    // to `MessageKeys`. The `satisfies ApiTopicShape[]` keeps TypeScript verifying
    // the NON-localized structure at this seam (drift there is a compile error,
    // honoring this file's promise); the `as` asserts only the key-narrowing the
    // api type cannot express. The api<->web catalogue parity guard + the key
    // completeness test verify every emitted key is a real, translated key at CI.
    const topics = await this.#namespace().listTopics.query();
    return topics satisfies readonly ApiTopicShape[] as readonly TopicDefinition[];
  }

  async submit(input: ComposeSubmitInput): Promise<ComposeSubmitResult> {
    const result = await this.#namespace().submitMessage.mutate({
      serviceLine: input.serviceLine,
      topicKey: input.topicKey,
      values: input.values,
      attachments: [...input.attachments],
    });
    // 051 boundary invariant: a routed SUCCESS must carry the created case id
    // (externalRef) — the attachment-upload step and the partial-failure
    // recovery link both key off it. The server always sets it on success
    // (is-case-routing-adapter); if a success ever arrived without it, showing
    // an unqualified success while the upload had no target and no recovery link
    // would be a fabricated success (P7). Downgrade that should-never-happen
    // case to an honest failure here, at the server↔web seam.
    if (
      result.outcome === 'success' &&
      (result.externalRef === undefined || result.externalRef.length === 0)
    ) {
      return {
        outcome: 'failure',
        memberMessage: result.memberMessage,
        auditEventId: result.auditEventId,
      };
    }
    return result;
  }
}
